<template>
  <div class="telefone-input-container">
    <label v-if="label" :for="id" class="form-label">
      {{ label }}
      <span v-if="required" class="required-indicator">*</span>
    </label>
    <div class="input-group">
      <imask-input
        :id="id"
        v-model="inputValue"
        class="form-control"
        :mask="currentMask"
        :unmask="unmask"
        :placeholder="placeholder"
        :class="{ 'is-invalid': showError }"
        :disabled="disabled"
        @accept="onAccept"
        @complete="onComplete"
        @input="onInput"
        @blur="onBlur"
      />
      <span v-if="showTypeIndicator" class="input-group-text telefone-type-indicator" :class="{ 'disabled': disabled }">
        {{ isCelular ? 'Celular' : 'Fixo' }}
      </span>
    </div>
    <small v-if="showError && errorMessage" class="text-danger">{{ errorMessage }}</small>
    <small v-else-if="helpText" class="form-text text-muted">{{ helpText }}</small>
  </div>
</template>

<script>
import { IMaskComponent } from 'vue-imask';
import { iMaskConfig } from '@/utils/constants';

export default {
  name: 'TelefoneInput',
  components: {
    'imask-input': IMaskComponent
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '(00) 0000-0000'
    },
    required: {
      type: Boolean,
      default: false
    },
    unmask: {
      type: Boolean,
      default: true
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: 'Por favor, insira um número de telefone válido'
    },
    helpText: {
      type: String,
      default: ''
    },
    showTypeIndicator: {
      type: Boolean,
      default: false
    },
    forceType: {
      type: String,
      default: null,
      validator: (value) => value === null || value === 'celular' || value === 'fixo'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputValue: this.value,
      isCelular: false,
      touched: false,
      masks: {
        celular: iMaskConfig.telemovel[0],
        fixo: iMaskConfig.telefone[0]
      }
    };
  },
  computed: {
    currentMask() {
      if (this.forceType === 'celular') {
        return this.masks.celular;
      } else if (this.forceType === 'fixo') {
        return this.masks.fixo;
      }
      
      // Detecta automaticamente com base no número inicial
      return this.detectPhoneType(this.inputValue);
    },
    isValid() {
      if (!this.inputValue) return !this.required;
      
      // Validação básica de telefone brasileiro
      const unmaskedValue = this.unmask ? this.inputValue : this.inputValue.replace(/\D/g, '');
      
      // Telefone deve ter 10 dígitos (8 dígitos + 2 do DDD)
      return unmaskedValue.length >= 10;
    },
    showError() {
      return this.touched && (this.error || (this.required && !this.isValid));
    }
  },
  watch: {
    value(newValue) {
      if (newValue !== this.inputValue) {
        this.inputValue = newValue;
        this.updatePhoneType();
      }
    }
  },
  methods: {
    detectPhoneType(value) {
      if (!value) return this.masks.celular;
      
      // Com a nova máscara, sempre usamos o mesmo formato de 10 dígitos (8+2)
      // Mantemos a variável isCelular como false para usar a máscara de telefone fixo
      this.isCelular = false;
      return this.masks.fixo;
    },
    updatePhoneType() {
      const mask = this.detectPhoneType(this.inputValue);
      this.isCelular = mask === this.masks.celular;
    },
    onAccept(e) {
      this.updatePhoneType();
      this.$emit('accept', e);
    },
    onComplete(e) {
      this.$emit('complete', e);
    },
    onInput() {
      this.$emit('input', this.inputValue);
      this.$emit('update:value', this.inputValue);
    },
    onBlur(e) {
      this.touched = true;
      this.$emit('blur', e);
    },
    reset() {
      this.inputValue = '';
      this.touched = false;
    }
  },
  created() {
    // Detecta o tipo de telefone inicialmente
    this.updatePhoneType();
  }
};
</script>

<style scoped>
.telefone-input-container {
  margin-bottom: 1rem;
}

.required-indicator {
  color: #dc3545;
  margin-left: 2px;
}

.telefone-type-indicator {
  min-width: 70px;
  display: flex;
  justify-content: center;
  background-color: #f8f9fa;
  color: #6c757d;
  font-size: 0.875rem;
}

.telefone-type-indicator.disabled {
  background-color: #e9ecef;
  color: #6c757d;
  opacity: 0.8;
}

/* Estilo para visualização em dispositivos móveis */
@media (max-width: 576px) {
  .input-group {
    flex-direction: column;
  }
  
  .telefone-type-indicator {
    margin-top: 0.25rem;
    border-radius: 0.25rem;
    width: 100%;
    justify-content: flex-start;
    padding: 0.25rem 0.5rem;
  }
}
</style> 